// Rage utilisation panel — Phase 2A second slice.
//
// Shows where the player's rage came from over the analysed window,
// grouped by source ability. Names + icons are async-upgraded via the
// /api/wow/lookup helper (same as the ability-breakdown table).

import type { RageSummary, RageSourceBucket } from '@shared/analysis';
import { t } from '@shared/i18n';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

const ICON_BASE = '/api/img/images/wow/icons/medium';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString();
}

function fmtPct(p: number): string {
  if (!Number.isFinite(p)) return '–';
  return `${(p * 100).toFixed(1)}%`;
}

// Same delta scheme as the ability table: ▲N green when slot B has more,
// ▼N red when less, hidden when equal. Same direction convention applies
// (more rage = better, so no inverted flag).
function deltaInt(diff: number): string {
  if (!Number.isFinite(diff) || diff === 0) return '';
  const arrow = diff > 0 ? '▲' : '▼';
  const cls = diff > 0 ? 'delta good' : 'delta bad';
  return ` <span class="${cls}">(${arrow}${Math.abs(diff).toLocaleString()})</span>`;
}
function deltaPct(diff: number): string {
  if (!Number.isFinite(diff) || Math.abs(diff) < 0.0005) return '';
  const arrow = diff > 0 ? '▲' : '▼';
  const cls = diff > 0 ? 'delta good' : 'delta bad';
  return ` <span class="${cls}">(${arrow}${(Math.abs(diff) * 100).toFixed(1)}%)</span>`;
}

function emptyBucket(ref: RageSourceBucket): RageSourceBucket {
  return { abilityID: ref.abilityID, events: 0, total: 0, share: 0 };
}

export function renderRagePanel(
  rage: RageSummary,
  container: HTMLElement,
  opts?: { compareTo?: RageSummary },
): void {
  container.replaceChildren();

  const cmpAll = opts?.compareTo;
  const cmpById = cmpAll ? new Map(cmpAll.bySource.map((b) => [b.abilityID, b])) : null;

  const summary = document.createElement('p');
  summary.className = 'analysis-summary muted';
  summary.textContent = t('rage.summary', {
    total: fmtNum(rage.totalGain),
    events: rage.bySource.reduce((acc, b) => acc + b.events, 0),
  });
  container.appendChild(summary);

  if (rage.bySource.length === 0 && (!cmpAll || cmpAll.bySource.length === 0)) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent = t('rage.empty');
    container.appendChild(p);
    return;
  }

  const table = document.createElement('table');
  table.className = 'rage-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('rage.col.source')}</th>
        <th>${t('rage.col.events')}</th>
        <th>${t('rage.col.total')}</th>
        <th>${t('rage.col.share')}</th>
        <th class="bar-col"></th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;

  // Reorder slot B's rows to mirror slot A's order. Sources slot A had
  // that slot B doesn't show as zero-rows (with red ▼ deltas reflecting
  // the gap). Sources slot B has that slot A didn't — different trinket
  // / potion / talent — get appended after the matched list so the user
  // sees them too.
  let ordered: RageSourceBucket[];
  if (cmpAll) {
    const byId = new Map(rage.bySource.map((b) => [b.abilityID, b]));
    const matchedIds = new Set<number>();
    ordered = [];
    for (const ref of cmpAll.bySource) {
      const b = byId.get(ref.abilityID);
      ordered.push(b ?? emptyBucket(ref));
      matchedIds.add(ref.abilityID);
    }
    for (const b of rage.bySource) {
      if (!matchedIds.has(b.abilityID)) ordered.push(b);
    }
  } else {
    ordered = rage.bySource;
  }

  for (const b of ordered) {
    const tr = document.createElement('tr');
    tr.className = 'rage-row';
    const pct = b.share * 100;
    const cmp = cmpById?.get(b.abilityID);
    const dEvents = cmp ? deltaInt(b.events - cmp.events) : '';
    const dTotal  = cmp ? deltaInt(b.total - cmp.total) : '';
    const dShare  = cmp ? deltaPct(b.share - cmp.share) : '';
    tr.innerHTML = `
      <td class="source">
        <a class="rage-link" target="_blank" rel="noopener" href="/api/wh/classic/spell=${b.abilityID}">
          <span class="icon-slot"></span>
          <span class="rage-name">spell ${b.abilityID}</span>
        </a>
      </td>
      <td class="num">${fmtNum(b.events)}${dEvents}</td>
      <td class="num strong">${fmtNum(b.total)}${dTotal}</td>
      <td class="num">${fmtPct(b.share)}${dShare}</td>
      <td class="bar-col"><div class="rage-bar" style="width:${pct.toFixed(1)}%"></div></td>
    `;
    tbody.appendChild(tr);

    // Tooltip on the source link.
    const link = tr.querySelector<HTMLAnchorElement>('a.rage-link');
    if (link) attachTooltip(link, 'spell', b.abilityID);

    // Async wowhead upgrade — get the localised name + icon for this ability.
    void lookup('spell', b.abilityID).then((r) => {
      if (r.name) {
        const n = tr.querySelector<HTMLSpanElement>('.rage-name');
        if (n) n.textContent = r.name;
      }
      if (r.icon) {
        const slot = tr.querySelector<HTMLSpanElement>('.icon-slot');
        if (slot) {
          const img = document.createElement('img');
          img.className = 'icon';
          img.width = 20;
          img.height = 20;
          img.alt = '';
          img.src = iconNameToUrl(r.icon);
          img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
          slot.replaceChildren(img);
        }
      } else {
        // Fallback: question-mark glyph so the row width stays consistent.
        const slot = tr.querySelector<HTMLSpanElement>('.icon-slot');
        if (slot) {
          const img = document.createElement('img');
          img.className = 'icon';
          img.width = 20;
          img.height = 20;
          img.alt = '';
          img.src = `${ICON_BASE}/inv_misc_questionmark.jpg`;
          slot.replaceChildren(img);
        }
      }
    });
  }
  container.appendChild(table);
}
